import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { useAxios } from '../../core/_axios';
import { 
  Building2, 
  Users, 
  MapPin, 
  Calendar,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  Sparkles,
  Heart,
  Home,
  Globe
} from 'lucide-react';

interface OnboardingFlowProps {
  onComplete: (data: OnboardingData) => void;
  onSkip: () => void;
  onAccountUpdate?: () => void;
  initialData?: OnboardingData;
}

interface OnboardingData {
  funeralHomeName?: string;
  location?: {
    city: string;
    state: string;
    country: string;
  };
  servicesPerMonth?: string;
  serviceTypes?: string[];
  otherServicesText?: string;
  teamSize?: string;
  currentTools?: string[];
  otherToolsText?: string;
  currentChallenges?: string[];
  otherChallengesText?: string;
  primaryGoals?: string[];
  hasIPad?: boolean;
  needsHelp?: string[];
}

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete, onSkip, onAccountUpdate, initialData }) => {
  const { currentUser } = useAuth();
  const axios = useAxios();
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<OnboardingData>(initialData || {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSavedStepRef = useRef<number>(-1);
  const hasDataRef = useRef<boolean>(false);

  // Track if we have meaningful data
  useEffect(() => {
    hasDataRef.current = Object.keys(data).length > 0;
  }, [data]);

  // Save when user navigates away or closes window
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasDataRef.current && currentStep > 0) {
        // Try to save synchronously with sendBeacon if available
        const payload = JSON.stringify({
          ...data,
          currentStep,
          abandoned: true
        });
        
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon('/api/v1/app/authenticated/onboarding-progress', blob);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Final save when component unmounts if data exists
      if (hasDataRef.current && currentStep > 0) {
        saveProgress(true);
      }
    };
  }, [currentStep]);

  const saveProgress = async (abandoned = false) => {
    // Only save if we have data and haven't already saved this step
    if (!hasDataRef.current || (currentStep === lastSavedStepRef.current && !abandoned)) {
      return;
    }

    try {
      await axios.post('/api/v1/app/authenticated/onboarding-progress', {
        ...data,
        currentStep,
        abandoned
      });
      lastSavedStepRef.current = currentStep;
      
      // If we saved the funeral home name, trigger account update to refresh header
      if (data.funeralHomeName && onAccountUpdate) {
        onAccountUpdate();
      }
    } catch (error) {
      console.error('Failed to save onboarding progress:', error);
    }
  };

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to SignatureGuestbook',
      subtitle: "Let's get your funeral home set up for success",
      icon: <img src="/design/web_logo_black_small.png" className="h-16" alt="SignatureGuestbook" />,
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-lg text-gray-700">
              We're thrilled to have you here! In the next few minutes, we'll help you 
              get everything set up so you can start creating meaningful digital guestbooks.
            </p>
          </div>
          <div className="bg-primary-light rounded-xl p-6">
            <h4 className="font-semibold mb-3">What we'll cover:</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-success mt-0.5" />
                <span>Your funeral home details</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-success mt-0.5" />
                <span>Service volume and types</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-success mt-0.5" />
                <span>How we can best support you</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'funeral-home',
      title: 'Tell us about your funeral home',
      subtitle: 'This helps us personalize your experience',
      icon: <Building2 className="w-16 h-16 text-primary" />,
      content: (
        <div className="space-y-6">
          <div>
            <label className="form-label mb-2">Funeral Home Name</label>
            <input
              type="text"
              className="input"
              placeholder="e.g., Smith & Sons Funeral Home"
              value={data.funeralHomeName || ''}
              onChange={(e) => setData({ ...data, funeralHomeName: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Focus next input (city)
                  const cityInput = document.querySelector('input[placeholder="City"]') as HTMLInputElement;
                  cityInput?.focus();
                }
              }}
              autoFocus
            />
          </div>
          
          <div>
            <label className="form-label mb-2">Location</label>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                className="input"
                placeholder="City"
                value={data.location?.city || ''}
                onChange={(e) => setData({ 
                  ...data, 
                  location: { ...data.location, city: e.target.value } as any
                })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Focus next input (state)
                    const stateInput = document.querySelector('input[placeholder="State/Province"]') as HTMLInputElement;
                    stateInput?.focus();
                  }
                }}
              />
              <input
                type="text"
                className="input"
                placeholder="State/Province"
                value={data.location?.state || ''}
                onChange={(e) => setData({ 
                  ...data, 
                  location: { ...data.location, state: e.target.value } as any
                })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleNext();
                  }
                }}
              />
            </div>
          </div>

          <div>
            <label className="form-label mb-2">How many locations do you have?</label>
            <div className="grid grid-cols-3 gap-3">
              {['1', '2-3', '4+'].map((option) => (
                <button
                  key={option}
                  className={`btn ${data.teamSize === option ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setData({ ...data, teamSize: option })}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'services',
      title: 'Your services',
      subtitle: 'Understanding your volume helps us recommend the right plan',
      icon: <Heart className="w-16 h-16 text-primary" />,
      content: (
        <div className="space-y-6">
          <div>
            <label className="form-label mb-2">How many services do you typically handle per month?</label>
            <div className="grid grid-cols-2 gap-3">
              {['1-10', '11-20', '21-50', '50+'].map((option) => (
                <button
                  key={option}
                  className={`btn ${data.servicesPerMonth === option ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setData({ ...data, servicesPerMonth: option })}
                >
                  {option} services
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="form-label mb-2">What types of services do you offer?</label>
            <div className="space-y-2">
              {[
                'Traditional',
                'Cremation',
                'Other services'
              ].map((type) => (
                <label key={type} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={data.serviceTypes?.includes(type) || false}
                    onChange={(e) => {
                      const types = data.serviceTypes || [];
                      if (e.target.checked) {
                        setData({ ...data, serviceTypes: [...types, type] });
                      } else {
                        setData({ ...data, serviceTypes: types.filter(t => t !== type) });
                      }
                    }}
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
            
            {/* Show textarea when "Other services" is selected */}
            {data.serviceTypes?.includes('Other services') && (
              <div className="mt-4 ml-7">
                <textarea
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-all duration-200"
                  rows={3}
                  placeholder="Please describe the other services you offer (e.g., memorial services, celebration of life, green burial, pre-planning, etc.)"
                  value={data.otherServicesText || ''}
                  onChange={(e) => setData({ ...data, otherServicesText: e.target.value })}
                  style={{ minHeight: '80px' }}
                />
                <p className="text-xs text-gray-500 mt-1">Feel free to list any additional services your funeral home provides</p>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'current-setup',
      title: 'Your guestbook setup',
      subtitle: 'Tell us how you currently collect signatures',
      icon: <Globe className="w-16 h-16 text-primary" />,
      content: (
        <div className="space-y-6">
          <div>
            <label className="form-label mb-2">What are you using for guestbooks now?</label>
            <div className="space-y-2">
              {[
                'Traditional paper guestbooks',
                'Messenger Expression',
                'Digital kiosk solutions',
                'Other'
              ].map((tool) => (
                <label key={tool} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={data.currentTools?.includes(tool) || false}
                    onChange={(e) => {
                      const tools = data.currentTools || [];
                      if (e.target.checked) {
                        setData({ ...data, currentTools: [...tools, tool] });
                      } else {
                        setData({ ...data, currentTools: tools.filter(t => t !== tool) });
                      }
                    }}
                  />
                  <span>{tool}</span>
                </label>
              ))}
            </div>
            
            {/* Show textarea when "Other" is selected */}
            {data.currentTools?.includes('Other') && (
              <div className="mt-4 ml-7">
                <textarea
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-all duration-200"
                  rows={3}
                  placeholder="Please describe what guestbook solution you're currently using (e.g., FuneralScreen, GatheringUs, Afterword, Keeper, custom software, etc.)"
                  value={data.otherToolsText || ''}
                  onChange={(e) => setData({ ...data, otherToolsText: e.target.value })}
                  style={{ minHeight: '80px' }}
                />
                <p className="text-xs text-gray-500 mt-1">This helps us understand your current workflow</p>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'complete',
      title: 'You\'re all set',
      subtitle: 'Let\'s get you started with SignatureGuestbook',
      icon: <Check className="w-16 h-16 text-success" />,
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-lg text-gray-700 mb-6">
              Thank you for taking the time to tell us about your funeral home. 
              We've customized your experience based on your needs.
            </p>
          </div>

          <div className="bg-success-light rounded-xl p-6">
            <h4 className="font-semibold mb-3">Here's what's ready for you:</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-success mt-0.5" />
                <span>Step-by-step setup checklist to guide you</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-success mt-0.5" />
                <span>Easy service creation to test the system</span>
              </li>
              {data.hasIPad === false && (
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-success mt-0.5" />
                  <span>iPad recommendations and setup instructions</span>
                </li>
              )}
            </ul>
          </div>

          <div className="text-center text-sm text-gray-600">
            <p>You can always update these preferences in your account settings.</p>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = async () => {
    if (currentStep === steps.length - 1) {
      setIsSubmitting(true);
      try {
        // Save onboarding data to backend
        await axios.post('/api/v1/app/authenticated/onboarding-complete', data);
        
        // Trigger account update to refresh header if funeral home name was set
        if (data.funeralHomeName && onAccountUpdate) {
          await onAccountUpdate();
        }
        
        onComplete(data);
      } catch (error) {
        console.error('Failed to save onboarding data:', error);
        onComplete(data); // Still complete even if save fails
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Save progress when moving to next step
      if (hasDataRef.current) {
        saveProgress();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      // Save progress when going back too
      if (hasDataRef.current) {
        saveProgress();
      }
      setCurrentStep(currentStep - 1);
    }
  };

  // Always allow proceeding - we want to capture whatever data we can
  const canProceed = () => true;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-primary-light via-white to-success-light flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary-light rounded-xl">
                {currentStepData.icon}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{currentStepData.title}</h2>
                <p className="text-gray-600">{currentStepData.subtitle}</p>
              </div>
            </div>
            <button
              onClick={async () => {
                await saveProgress(true);
                onSkip();
              }}
              className="btn btn-icon btn-light rounded-full"
              title="Skip onboarding"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-primary to-success h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handleBack}
                  className="btn btn-light flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              <button
                onClick={async () => {
                  await saveProgress(true);
                  onSkip();
                }}
                className="btn btn-link text-gray-600"
                disabled={isSubmitting}
              >
                Skip for now
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Step {currentStep + 1} of {steps.length}
              </span>
              <button
                onClick={handleNext}
                className="btn btn-primary flex items-center gap-2"
                disabled={isSubmitting}
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    {isSubmitting ? 'Getting started...' : 'Get Started'}
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;